import type { ColumnDef } from '@tanstack/react-table';
import { format, isValid, parseISO } from 'date-fns';
import { createSerialColumn } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { StatusBadge } from './StatusBadge';
import { formatDisplayDate, formatTime } from '@/utils/date';
import type { AttendanceDay, AttendanceStatus } from '../types/attendance.types';

function sameCalendarDay(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return true;

  const first = parseISO(a);
  const second = parseISO(b);
  if (!isValid(first) || !isValid(second)) return true;

  return format(first, 'yyyy-MM-dd') === format(second, 'yyyy-MM-dd');
}

function formatOutTime(outTime: string | null, inTime: string | null): string {
  if (outTime === null) return formatTime(outTime);
  if (sameCalendarDay(inTime, outTime)) return formatTime(outTime);

  const parsed = parseISO(outTime);
  if (!isValid(parsed)) return formatTime(outTime);

  return format(parsed, 'dd MMM, hh:mm a');
}

export const employeeReportColumns: ColumnDef<AttendanceDay, unknown>[] = [
  createSerialColumn<AttendanceDay>(),
  {
    accessorKey: 'date',
    header: 'Date',
    cell: ({ getValue }) => formatDisplayDate(getValue() as string),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => <StatusBadge status={getValue() as AttendanceStatus} />,
  },
  {
    accessorKey: 'inTime',
    header: 'In Time',
    cell: ({ getValue }) => formatTime(getValue() as string | null),
  },
  {
    accessorKey: 'outTime',
    header: 'Out Time',
    cell: ({ row }) => formatOutTime(row.original.outTime, row.original.inTime),
  },
  {
    accessorKey: 'workingHour',
    header: 'Working Hours',
    cell: ({ getValue }) => getValue() ?? <EmptyValue />,
  },
  {
    id: 'remarks',
    header: 'Remarks',
    cell: () => <EmptyValue />,
  },
];
